import { FiEdit, FiExternalLink } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { ACTIONED_BY_OPTIONS, STATE_OPTIONS } from '@/config';
import { ROUTES } from '@/config/routes';
import { AsyncNormalSelect, Button, Select } from '@/core/ui';
import type { RecordReport } from '@/graphql/gql/graphql';
import { useContentReview } from '@/store';
import { useGlobal } from '@/store/global';
import { getDbTypeHashFragment } from '@/utils';
import { formatDateFromUnix } from '@/utils/date';
import { ReasonCell } from '../../components/reason-cell';
import { ReportActions } from './report-actions';

type CellRendererProps = {
  report: RecordReport;
  columnKey: string;
  serialNo: number;
  onStateChange: (reportId: string, val: number | null) => Promise<void>;
  onActionedByChange: (reportId: string, val: number | null) => Promise<void>;
  onMembersChange: (reportId: string, keys: any) => Promise<void>;
  onDelete: (reportId: string | number) => Promise<void>;
  updateLoading: boolean;
  deleteLoading: boolean;
};

type FeedbackCellProps = {
  report: RecordReport;
  cellValue: string;
};

const FeedbackCell: React.FC<FeedbackCellProps> = ({ report, cellValue }) => {
  const openFeedbackModal = useContentReview.use.openFeedbackModal();

  return (
    <div className='relative bg-content1 rounded-lg p-2 border border-default-200'>
      <span className='text-sm max-w-60 wrap-break-word rounded-lg p-1 font-mono text-default-800'>
        {cellValue || '---'}
      </span>
      <Button
        isIconOnly
        size='sm'
        variant='light'
        className='min-w-6 w-6 h-6 absolute top-2 right-2'
        onPress={() => {
          openFeedbackModal(report.id?.toString() ?? '', cellValue ?? '', 'comment');
        }}
      >
        <FiEdit className='size-3.5 text-default-500' />
      </Button>
    </div>
  );
};

export const ReportTableCell: React.FC<CellRendererProps> = ({
  report,
  columnKey,
  serialNo,
  onStateChange,
  onActionedByChange,
  onMembersChange,
  onDelete,
  deleteLoading,
}) => {
  const dbType = useGlobal.use.dbType();

  switch (columnKey) {
    case 'srNo':
      return <span className='text-sm font-medium text-default-600'>{serialNo}</span>;

    case 'id':
      return <span className='text-sm font-mono text-default-600'>{report.id}</span>;

    case 'comment':
      return <FeedbackCell report={report} cellValue={report.comment ?? ''} />;

    case 'reason':
      return (
        <ReasonCell
          reviewId={report.id?.toString() ?? ''}
          reasonText={report.reason ?? ''}
          lineClamp={4}
          buttonVariant='solid'
          buttonColor='secondary'
          iconClassName='text-white'
        />
      );

    case 'recordId':
      return report.recordId ? (
        <Link
          target='_blank'
          rel='noopener noreferrer'
          to={
            ROUTES.CONTENT.EDIT.QUESTION.replace(':id', String(report.recordId)) +
            getDbTypeHashFragment('global-state', { dbType })
          }
          className='flex items-center gap-2 text-sm text-default-600 hover:text-default-800 hover:underline'
        >
          <span className='font-mono'>{report.recordId}</span>
          <FiExternalLink className='size-3.5' />
        </Link>
      ) : (
        <span className='text-sm text-default-400'>-</span>
      );

    case 'actionedBy':
      return (
        <div className='w-full'>
          <Select
            size='sm'
            variant='bordered'
            selectionMode='single'
            // popoverProps={{ classNames: { content: ' !p-1.5 !w-64 text-xs' } }}
            selectedKeys={report.actionedBy != null ? new Set([String(report.actionedBy)]) : new Set()}
            onSelectionChange={(keys) => {
              const arr = Array.from(keys as Set<string>);
              const raw = arr[0];
              const next = raw ? Number.parseInt(raw, 10) : NaN;
              if (Number.isFinite(next as number)) {
                onActionedByChange(report.id?.toString() ?? '', next as number);
              }
            }}
            options={ACTIONED_BY_OPTIONS}
            // className='w-32 text-sm'
          />
        </div>
      );

    case 'member': {
      const members = report.members ?? [];
      const currentIds = members.map((m) => m?.userId).filter((id): id is number => id != null);
      const names = members.map((m) => m?.user?.firstName).filter((name): name is string => name != null);

      return (
        <div className='flex flex-col gap-1 mt-5'>
          <AsyncNormalSelect
            size='sm'
            variant='bordered'
            description={names.join(', ')}
            name='members'
            groupBy='options'
            addValuePrefix={false}
            queryKey='admins'
            selectionMode='multiple'
            selectedKeys={new Set(currentIds.map((id) => String(id)))}
            onSelectionChange={(keys) => onMembersChange(report.id?.toString() ?? '', keys)}
            className='w-32 text-xs'
            popoverProps={{ classNames: { content: ' !p-1.5 !w-64 text-xs' } }}
            classNames={{ listbox: 'text-xs' }}
          />
        </div>
      );
    }

    case 'state':
      return (
        <div className='w-full'>
          <Select
            size='sm'
            variant='bordered'
            selectionMode='single'
            // popoverProps={{ classNames: { content: ' !p-1.5 !w-64 text-xs' } }}
            selectedKeys={report.state != null ? new Set([String(report.state)]) : new Set()}
            onSelectionChange={(keys) => {
              const arr = Array.from(keys as Set<string>);
              const raw = arr[0];
              const next = raw ? Number.parseInt(raw, 10) : NaN;
              if (Number.isFinite(next as number)) {
                onStateChange(report.id?.toString() ?? '', next as number);
              }
            }}
            options={STATE_OPTIONS}
            className='w-32 text-sm'
          />
        </div>
      );

    case 'createdAt':
      return report.createdAt ? (
        <div className='flex flex-col'>
          <span className='text-sm text-default-600'>
            {formatDateFromUnix(report.createdAt, 'DD MMM YY | HH:mm')}
          </span>
          <span className='text-xs text-default-400'>
            <Link
              className='underline hover:text-primary-600 transition-colors'
              to={ROUTES.USER.INFO.concat('?id=', String(report.createdBy ?? ''))}
              target='_blank'
              rel='noopener noreferrer'
            >
              User Info: {report.createdBy}
            </Link>
          </span>
        </div>
      ) : (
        <span className='text-sm text-default-400'>-</span>
      );

    case 'actions':
      return (
        <div className='flex items-center gap-2 justify-center'>
          <ReportActions
            report={report}
            isLoading={deleteLoading}
            onAction={async (reportId) => {
              if (reportId) {
                await onDelete(reportId);
              }
            }}
          />
        </div>
      );

    default:
      return <span className='text-sm text-default-400'>-</span>;
  }
};
